/**
 * @fileoverview Component for displaying a study verification modal.
 * This component includes a form for confirming a study change and a button for confirming the creation.
 */

import { XMarkIcon } from '@heroicons/react/20/solid'
import { ChangeEvent, useState } from 'react'
import { States } from '../../../config'
import { useModal } from '../../../hooks/useModal'
import { PrimaryButton } from '../../elements/PrimaryButton'
import { RegularButton } from '../../elements/RegularButton'
import { TextInput } from '../../form/TextInput'
import StatusDropdown from '../../StatusDropdown'

/**
 * @interface State
 * @description The state of the study verification modal.
 * @property {string} name - The name of the study.
 * @property {string} value - The value of the study.
 * @property {JSX.Element} listIcon - The icon of the study in the list.
 */
type State = {
    name: string
    value: string
    listIcon: JSX.Element
}

/**
 * @interface StudyVerificationModalProps
 * @description Props for the StudyVerificationModal component.
 * @property {Object} transition - The transition object containing the current and new study.
 * @property {State} transition.from - The current study state.
 * @property {State} transition.to - The new study state.
 * @property {States} status - The current status.
 * @property {function} onSubmit - The function to call when the form is submitted.
 * Takes from state, to state, annotation text, status, and optional credentials.
 * Returns a Promise that resolves when submission is complete.
 */

interface StatusVerificationModalProps {
    transition: {
        from: State
        to: State
    }
    status: States
    onSubmit: (
        from: string,
        to: string,
        annotation: string,
        status: States,
        credentials?: { username?: string; password?: string }
    ) => Promise<void>
}

/**
 * @function StudyVerificationModal
 * @description Component for displaying a study verification modal.
 * @param {StudyVerificationModalProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the study verification modal.
 */
export function StudyVerificationModal({
    transition,
    onSubmit,
    status: defaultStatus,
}: StatusVerificationModalProps) {
    const [, close] = useModal()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [changelog, setChangelog] = useState('')
    const [status, setStatus] = useState<States>(defaultStatus)
    const [error, setError] = useState<string>()

    function onChangeStatus(newStatus: States) {
        setStatus(newStatus)
    }

    return (
        <div className="w-[416px]">
            <div className="flex justify-between items-center gap-4">
                <h2 className="text-xl font-semibold">Confirmation required</h2>
                <button className="w-6 h-6" onClick={close}>
                    <XMarkIcon />
                </button>
            </div>
            <p className="pt-3 pb-4 text-gray-500 text-sm">
                Following changes require you to state the reason and provide your credentials
                before you proceed:
            </p>

            <div className="grid grid-cols-[auto,1fr] justify-center gap-y-2 p-4 rounded-md bg-gray-50">
                <p className="text-gray-600 text-sm">Current study</p>
                <div className="flex items-center ml-6">
                    <span>{transition.from.listIcon}</span>
                    <div className="ml-2">
                        <div className="flex text-sm font-medium text-gray-900">
                            <span>{transition.from.name}</span>
                        </div>
                    </div>
                </div>

                <p className="text-gray-600 text-sm">New study</p>
                <div className="flex items-center ml-6">
                    <span>{transition.to.listIcon}</span>
                    <div className="ml-2">
                        <div className="flex text-sm font-medium text-gray-900">
                            <span>{transition.to.name}</span>
                        </div>
                    </div>
                </div>
            </div>

            <section className="flex flex-col mt-6">
                <label htmlFor="changelog" className="text-sm font-medium pb-1">
                    Annotation <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                    id="changelog"
                    placeholder="What is the reason for this change?"
                    value={changelog}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        setChangelog(e.target.value)
                        setError(undefined)
                    }}
                    className="block h-[75px] text-sm p-2.5 w-full text-gray-900 rounded-lg border border-gray-300 focus:border-indigo-500  focus:ring-indigo-500"
                />
            </section>

            <section className="mt-6">
                <label htmlFor="email" className="text-sm font-medium pb-1">
                    Stage
                </label>
                <div className="flex flex-col col-span-2">
                    <StatusDropdown name="stage" current={status} onChange={onChangeStatus} />
                </div>
            </section>

            <section className="mt-6">
                <label htmlFor="email" className="text-sm font-medium pb-1">
                    Your email and password
                </label>
                <div className="-space-y-px rounded-md shadow-sm">
                    <div>
                        <TextInput
                            id="login"
                            name="email"
                            type="text"
                            autoComplete="email"
                            required
                            className="rounded-t-md px-3 py-2"
                            placeholder="Email"
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value)
                                setError(undefined)
                            }}
                            validationError="Please, fill in your login (SID)"
                            error={error}
                        />
                    </div>
                    <div>
                        <TextInput
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="password"
                            required
                            className="rounded-b-md px-3 py-2"
                            placeholder="Password"
                            value={password}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setPassword(e.target.value)
                            }
                            validationError="Please, fill in your password"
                            error={error}
                        />
                    </div>
                </div>
                {error && <p className="pt-1 text-xs text-red-500">{error}</p>}
            </section>

            <section className="flex flex-row col-span-2 gap-2 items-stretch mt-8">
                <RegularButton className="flex grow" onClick={close}>
                    Cancel
                </RegularButton>
                <PrimaryButton
                    className="flex grow"
                    onClick={async () => {
                        if (!email || !password) {
                            setError(
                                'You should provide your email and password before you proceed.'
                            )
                            return
                        }
                        try {
                            await onSubmit(
                                transition.from.value,
                                transition.to.value,
                                changelog,
                                status,
                                {
                                    username: email,
                                    password,
                                }
                            )
                            close()
                        } catch (e) {
                            setError('Incorrect email or password.')
                        }
                    }}
                >
                    Confirm
                </PrimaryButton>
            </section>
        </div>
    )
}
